// ---------------------------------------------------------------------------
// What the till knows about its own connection.
//
// One store, read by the status strip in the header, the POS, and the queue
// screen. It holds only what the screen needs; the sales themselves live in
// IndexedDB, because a Zustand store is memory and memory does not survive the
// tab being closed.
//
// `navigator.onLine` is treated as a HINT, not as truth. It reports whether
// the machine has a network interface up, which on a shop's Wi-Fi is very
// often true while nothing can actually reach the server. So the store also
// records whether the last real request succeeded, and the strip says
// "offline" when either says so.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import type { PendingHeldOp, PendingSale } from '../lib/offlineDb';
import type { PrintPlan } from '../hooks/useSalePrinting';

export type SyncPhase = 'idle' | 'syncing' | 'failed';

export interface LastSyncResult {
  at: string;
  synced: number;
  duplicates: number;
  failed: number;
}

interface OfflineState {
  /// What the browser claims.
  browserOnline: boolean;
  /// Whether the last attempt to reach the server actually worked. Null until
  /// something has been tried.
  serverReachable: boolean | null;

  phase: SyncPhase;
  pending: PendingSale[];
  /// Held-cart operations waiting to go over, including the ones the server
  /// refused and the ones in conflict. Kept until a person deals with them.
  heldOps: PendingHeldOp[];
  lastSync: LastSyncResult | null;
  /// Set when a whole sync attempt failed, as opposed to individual sales.
  lastError: string | null;

  /// Whether the catalogue has been captured, and when.
  catalogueCapturedAt: string | null;
  /// How many receipt numbers this till holds for selling offline.
  reservedReceipts: number;
  /// Bench tickets the server owes for sales that were synchronised - the
  /// receipt printed offline, the tickets could not, so they print now.
  ticketPlans: PrintPlan[];

  setBrowserOnline: (online: boolean) => void;
  setServerReachable: (reachable: boolean | null) => void;
  setPhase: (phase: SyncPhase) => void;
  setPending: (pending: PendingSale[]) => void;
  setHeldOps: (heldOps: PendingHeldOp[]) => void;
  setLastSync: (result: LastSyncResult) => void;
  setLastError: (message: string | null) => void;
  setCatalogueCapturedAt: (at: string | null) => void;
  setReservedReceipts: (count: number) => void;
  addTicketPlans: (plans: PrintPlan[]) => void;
  removeTicketPlan: (saleId: string | null) => void;
}

export const useOfflineStore = create<OfflineState>((set) => ({
  browserOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
  serverReachable: null,
  phase: 'idle',
  pending: [],
  heldOps: [],
  lastSync: null,
  lastError: null,
  catalogueCapturedAt: null,
  reservedReceipts: 0,
  ticketPlans: [],

  setBrowserOnline: (browserOnline) => set({ browserOnline }),
  setServerReachable: (serverReachable) => set({ serverReachable }),
  setPhase: (phase) => set({ phase }),
  setPending: (pending) => set({ pending }),
  setHeldOps: (heldOps) => set({ heldOps }),
  setLastSync: (lastSync) => set({ lastSync, lastError: null }),
  setLastError: (lastError) => set({ lastError }),
  setCatalogueCapturedAt: (catalogueCapturedAt) => set({ catalogueCapturedAt }),
  setReservedReceipts: (reservedReceipts) => set({ reservedReceipts }),
  // One plan per sale: a lost answer replayed by the next sweep must not
  // mount the same tickets twice (the job keys would refuse them anyway).
  addTicketPlans: (plans) =>
    set((state) => ({
      ticketPlans: [...state.ticketPlans, ...plans.filter((plan) => !state.ticketPlans.some((held) => held.saleId === plan.saleId))],
    })),
  removeTicketPlan: (saleId) => set((state) => ({ ticketPlans: state.ticketPlans.filter((plan) => plan.saleId !== saleId) })),
}));

/// Whether the till should behave as offline.
///
/// Deliberately pessimistic: a browser that says it is online but whose last
/// request failed is treated as offline, because that is what the cashier is
/// experiencing. Being wrong in this direction queues a sale that could have
/// been sent, which is harmless - it syncs seconds later. Being wrong the
/// other way loses the sale.
///
/// THE SERVER'S OWN ANSWER OUTRANKS THE BROWSER'S OPINION. `navigator.onLine`
/// is Windows saying whether it believes the machine has a network; with the
/// router off it says no - and the shop's server is on THIS machine, on
/// 127.0.0.1, answering perfectly, with the receipt printer on a USB cable
/// beside it. A probe that reached the server is a fact; "no network" is a
/// guess about the internet. So a fresh `true` from the probe keeps the till
/// online whatever the browser thinks, and the browser's "offline" decides
/// only while nothing has been heard from the server since it fired.
export function isEffectivelyOffline(state: {
  browserOnline: boolean;
  serverReachable: boolean | null;
}): boolean {
  if (state.serverReachable === false) return true;
  if (state.serverReachable === true) return false;
  return !state.browserOnline;
}

/// How many sales are waiting, and how many of those have a problem.
export function queueSummary(pending: PendingSale[]): { total: number; failed: number } {
  return {
    total: pending.length,
    failed: pending.filter((sale) => sale.failedReason !== undefined).length,
  };
}
